import * as stateManagement from '#packages/stateManagement';
import * as util from '#packages/util';
import { translate } from '#packages/i18n';

import * as galleriesData from './galleriesData';

import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from 'types/documentServices';
import { EditorPlatformHostIntegrationAPI } from '@wix/editor-platform-host-integration-apis';

function getGalleryDisplayName(
  editorAPI: EditorAPI,
  compRef: CompRef,
  compType: string,
): string {
  if (!galleriesData.map.hasOwnProperty(compType)) {
    return;
  }

  const compSkin = stateManagement.components.selectors.getSkin(
    compRef,
    editorAPI.dsRead,
  );

  const compSkinsMap =
    galleriesData.map[compType as keyof typeof galleriesData.map];
  const compSkinDisplayName =
    compSkin &&
    (Array.isArray(compSkinsMap)
      ? compSkinsMap.find((skins) => !!skins[compSkin])
      : compSkinsMap[compSkin as keyof typeof compSkinsMap])?.[compSkin];

  return compSkinDisplayName && translate(compSkinDisplayName);
}

function getPlatformDisplayName(editorAPI: EditorAPI, compRef: CompRef) {
  return stateManagement.components.selectors.getPlatformDisplayName(
    compRef,
    editorAPI.dsRead,
  );
}

export function getDisplayName(
  editorAPI: EditorAPI,
  compRef: CompRef,
  compTypeOverride?: string,
): string {
  const dsRead = editorAPI.dsRead;

  const mainCompRef = util.controlsUtils.getFirstControllableComponent(
    editorAPI,
    compRef,
  );
  const mainCompType: string =
    compTypeOverride || dsRead.components.getType(mainCompRef);

  const displayName =
    getGalleryDisplayName(editorAPI, mainCompRef, mainCompType) ||
    getPlatformDisplayName(editorAPI, mainCompRef);

  if (displayName) {
    return displayName;
  }

  if (typeof editorAPI.compNamePlugins[mainCompType] === 'function') {
    return editorAPI.compNamePlugins[mainCompType](mainCompRef);
  }

  const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
  const unifiedComponentName =
    platformAPI.components.getComponentIdentifiers(compRef)?.name;
  if (unifiedComponentName) {
    return unifiedComponentName;
  }

  const compDefinition = dsRead.components.getDefinition(mainCompType);

  return (
    // @ts-expect-error
    compDefinition?.translatedLabelDisplayName ||
    translate(
      compDefinition?.labelDisplayName ||
        `component_label_${mainCompType.split('.').pop()}`,
    )
  );
}
